import { AuthenticatedContext, User } from '@sec-loans-ui/common/types'
import { applicationDataToWizardData } from '@sec-loans-ui/ifp-loans/utils'
import { assertValue } from '@shared/helpers'
import { IfpGetApplicationResponse, IfpLoan } from '@shared/sec-loans'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import {
  type EditStepEvent as CommonEditStepEvent,
  type NextStepEvent as CommonNextStepEvent,
  type PreviousStepEvent,
  YesNo,
} from '@ui-common/types'
import { assign, setup } from 'xstate'

import { IfpBasicInfoFormData } from '../steps/ifp-info/types'
import { IfpWizardData } from './types'

export enum WizardStep {
  EntryStep = 'entry',
  GetApplicationStep = 'getApplication',
  StatusStep = 'status',
  IfpInfoStep = 'ifpInfo',
  HouseholdInfoStep = 'householdInfo',
  ApplicantInfoStep = 'applicantInfo',
  ConsentStep = 'consent',
  EndStep = 'end',
}

export enum IfpWizardType {
  Long = 'long',
  Short = 'short',
}

type EditableStep = WizardStep.IfpInfoStep | WizardStep.HouseholdInfoStep | WizardStep.ApplicantInfoStep

export interface WizardContext extends Partial<AuthenticatedContext> {
  user: User
  applicationId: string
  loan: IfpLoan
  isNewApplication: boolean
  wizardData?: IfpWizardData
  isEditing?: boolean
  wizardType?: IfpWizardType
  currentApplicantInfoIndex: number
}

type NextStepEvent = CommonNextStepEvent<WizardContext['wizardData']>
type EditStepEvent = CommonEditStepEvent<EditableStep>
type BackToStatusEvent = { type: 'BACK_TO_STATUS' }
type GetApplicationEvent = GetDataDoneEvent<IfpGetApplicationResponse>

type WizardEvent = GetApplicationEvent | NextStepEvent | PreviousStepEvent | EditStepEvent | BackToStatusEvent

export type WizardMachineType = ReturnType<typeof createWizardMachine>

// return type is derived from setup fn and used in WizardMachineType
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = () =>
  setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
      input: {} as WizardContext,
    },
    actions: {
      enableEdit: assign(() => {
        return { isEditing: true }
      }),
      disableEdit: assign(() => {
        return { isEditing: false }
      }),
      saveFormData: assign(({ context }, wizardData: Partial<IfpWizardData>) => {
        return {
          wizardData: {
            ...context.wizardData,
            ...wizardData,
          },
        }
      }),
      setShortWizardType: assign(() => ({ wizardType: IfpWizardType.Short })),
      setLongWizardType: assign(() => ({ wizardType: IfpWizardType.Long })),
      incrementCurrentApplicantInfoIndex: assign(({ context }) => ({
        currentApplicantInfoIndex: context.currentApplicantInfoIndex + 1,
      })),
      decrementCurrentApplicantInfoIndex: assign(({ context }) => ({
        currentApplicantInfoIndex: context.currentApplicantInfoIndex - 1,
      })),
      resetCurrentApplicantInfoIndex: assign(() => ({ currentApplicantInfoIndex: 0 })),
    },
    guards: {
      isEditing: ({ context }) => !!context.isEditing,
      shouldGoToShortWizard: ({ context }, { durationInMonths, hasFinancialDifficulties }: IfpBasicInfoFormData) => {
        const { principalExemptionMonthsLeft } = context.loan
        return !!principalExemptionMonthsLeft && Number(durationInMonths) <= 3 && hasFinancialDifficulties === YesNo.No
      },
      isShortWizard: ({ context }) => context.wizardType === IfpWizardType.Short,
      shouldGoToNextApplicantInfo: ({ context }) => {
        const { otherDebtors } = assertValue(context.loan, 'context.loan')
        const totalDebtorsCount = otherDebtors.length + 1
        const { currentApplicantInfoIndex } = context

        return currentApplicantInfoIndex + 1 < totalDebtorsCount
      },
      shouldGoToPreviousApplicantInfo: ({ context }) => {
        const { currentApplicantInfoIndex } = context
        return currentApplicantInfoIndex > 0
      },
    },
  }).createMachine({
    id: 'ifpLoansWizardMachine',
    initial: WizardStep.EntryStep,
    context: ({ input }) => ({ ...input, currentApplicantInfoIndex: 0 }),
    on: {
      BACK_TO_STATUS: { target: `.${WizardStep.StatusStep}` },
    },
    states: {
      [WizardStep.EntryStep]: {
        id: WizardStep.EntryStep,
        always: [
          {
            guard: ({ context }) => context.isNewApplication,
            target: WizardStep.StatusStep,
          },
          {
            target: WizardStep.GetApplicationStep,
          },
        ],
      },
      [WizardStep.GetApplicationStep]: {
        on: {
          GET_DATA_DONE: [
            {
              actions: {
                type: 'saveFormData',
                params: ({ event }: { event: GetApplicationEvent }) =>
                  applicationDataToWizardData(assertValue(event.data.data, 'GetApplicationEvent.data.data')),
              },
              target: WizardStep.StatusStep,
            },
          ],
        },
      },
      [WizardStep.StatusStep]: {
        entry: { type: 'disableEdit' },
        on: {
          NEXT_STEP: { target: WizardStep.IfpInfoStep },
          // SUBMIT: { target: WizardStep.ConsentStep },
          EDIT: [],
        },
      },
      [WizardStep.IfpInfoStep]: {
        on: {
          PREVIOUS_STEP: [{ guard: 'isEditing', target: WizardStep.StatusStep }],
          NEXT_STEP: [
            {
              target: WizardStep.ApplicantInfoStep,
              guard: {
                type: 'shouldGoToShortWizard',
                params: ({ event }) => assertValue(event.data?.basicInfo, 'event.data.basicInfo'),
              },
              actions: [
                {
                  type: 'saveFormData',
                  params: ({ event }) => assertValue(event.data, 'event.data'),
                },
                'setShortWizardType',
              ],
            },
            {
              target: WizardStep.HouseholdInfoStep,
              actions: [
                {
                  type: 'saveFormData',
                  params: ({ event }) => assertValue(event.data, 'event.data'),
                },
                'setLongWizardType',
              ],
            },
          ],
        },
      },
      [WizardStep.HouseholdInfoStep]: {
        on: {
          PREVIOUS_STEP: [{ guard: 'isEditing', target: WizardStep.StatusStep }, { target: WizardStep.IfpInfoStep }],
          NEXT_STEP: {
            target: WizardStep.ApplicantInfoStep,
            actions: {
              type: 'saveFormData',
              params: ({ event }) => assertValue(event.data, 'event.data'),
            },
          },
        },
      },
      [WizardStep.ApplicantInfoStep]: {
        on: {
          PREVIOUS_STEP: [
            { guard: 'isEditing', target: WizardStep.StatusStep },
            {
              guard: 'shouldGoToPreviousApplicantInfo',
              target: WizardStep.ApplicantInfoStep,
              actions: ['decrementCurrentApplicantInfoIndex'],
            },
            { guard: 'isShortWizard', target: WizardStep.IfpInfoStep },
            { target: WizardStep.HouseholdInfoStep },
          ],
          NEXT_STEP: [
            {
              guard: 'shouldGoToNextApplicantInfo',
              actions: [
                {
                  type: 'saveFormData',
                  params: ({ event }) => assertValue(event.data, 'event.data'),
                },
                'incrementCurrentApplicantInfoIndex',
              ],
              target: WizardStep.ApplicantInfoStep,
            },
            {
              target: WizardStep.StatusStep,
              actions: [
                {
                  type: 'saveFormData',
                  params: ({ event }) => assertValue(event.data, 'event.data'),
                },
                'resetCurrentApplicantInfoIndex',
              ],
            },
          ],
        },
      },
      [WizardStep.ConsentStep]: {
        on: {
          NEXT_STEP: { target: WizardStep.EndStep },
        },
      },
      [WizardStep.EndStep]: {},
    },
  })
